import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { supabase } from "@/lib/supabase";
import { checkIsAdmin } from "@/lib/admin";

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminCheckComplete, setIsAdminCheckComplete] = useState(false);
  const adminCheckSeqRef = useRef(0);
  const lastAdminCheckUserIdRef = useRef(null);
  const [adminMode, setAdminMode] = useState(() => {
    // Initialize from localStorage
    const savedAdminMode = localStorage.getItem("adminMode");
    return savedAdminMode === "true";
  });

  const checkAdminStatus = async (userData) => {
    if (!userData) {
      lastAdminCheckUserIdRef.current = null;
      setIsAdmin(false);
      setAdminMode(false);
      localStorage.removeItem("adminMode");
      setIsAdminCheckComplete(true);
      return;
    }

    const userIdChanged = lastAdminCheckUserIdRef.current !== userData.id;
    if (userIdChanged) {
      lastAdminCheckUserIdRef.current = userData.id;
      setIsAdminCheckComplete(false);
    }

    const seq = ++adminCheckSeqRef.current;
    try {
      const isUserAdmin = await checkIsAdmin(userData.id);
      if (seq !== adminCheckSeqRef.current) return;

      setIsAdmin(isUserAdmin);
      if (!isUserAdmin) {
        setAdminMode(false);
        localStorage.removeItem("adminMode");
      }
    } catch (error) {
      console.error("Error checking admin status:", error);
      if (seq !== adminCheckSeqRef.current) return;
      setIsAdmin(false);
      setAdminMode(false);
      localStorage.removeItem("adminMode");
    } finally {
      if (seq === adminCheckSeqRef.current) {
        setIsAdminCheckComplete(true);
      }
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      checkAdminStatus(session?.user);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setTimeout(async () => {
        setSession(session);

        // Only reset admin mode on actual sign out
        if (event === "SIGNED_OUT") {
          lastAdminCheckUserIdRef.current = null;
          setIsAdmin(false);
          setAdminMode(false);
          localStorage.removeItem("adminMode");
          setIsAdminCheckComplete(true);
        } else if (event === "SIGNED_IN") {
          // On fresh sign in (not refresh), check admin status
          checkAdminStatus(session?.user);
        }
        // For INITIAL_SESSION and TOKEN_REFRESHED, don't do anything special
        // The adminMode from localStorage is already set

        setLoading(false);
      }, 0);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session) {
      setUser(session?.user ?? null);
      checkAdminStatus(session?.user);
      setLoading(false);
    } else {
      lastAdminCheckUserIdRef.current = null;
      setUser(null);
      setIsAdmin(false);
      setAdminMode(false);
      localStorage.removeItem("adminMode");
      setIsAdminCheckComplete(true);
      setLoading(false);
    }
  }, [session]);

  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
        },
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error("Sign up error:", error);
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error("Sign in error:", error?.message || error);
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      return { error: null };
    } catch (error) {
      console.error("Sign out error:", error);
      return { error };
    }
  };

  const resetPassword = async (email) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error("Reset password error:", error);
      return { data: null, error };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error("Update password error:", error);
      return { data: null, error };
    }
  };

  const toggleAdminMode = () => {
    if (isAdmin) {
      setAdminMode((prev) => {
        const newMode = !prev;
        // Save to localStorage
        if (newMode) {
          localStorage.setItem("adminMode", "true");
        } else {
          localStorage.removeItem("adminMode");
        }
        return newMode;
      });
    }
  };

  const value = {
    user,
    session,
    loading,
    isAdmin,
    isAdminCheckComplete,
    adminMode,
    toggleAdminMode,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
